/**
 * ProblemSearchPanel — problem-hub 좌측 마스터(①문제 리스트 · ②검색 · AI 모아보기 · 분석 진입).
 *
 * 계약: BE-20260825-problem-hub-integrated.md §2 — 필터를 비우면 전체 목록, 주면 검색.
 * 같은 엔드포인트라 화면도 "목록"과 "검색"을 나누지 않는다. 검색은 Enter·버튼 둘 다.
 * 2026-08-26 갱신: BE-20260826-audit-fixes-folder-tree-v2apis.md §1-5(검색)·§3(권한).
 *
 * 2026-08-29 갱신: **접기를 걷고 시험지 구성 모달(PaperComposeModal)과 같은 배치로 맞췄다**
 * (오너 지시 「그대로 적용」). 두 화면은 같은 엔드포인트를 치는 형제인데 검색 조작이 갈려 있어서,
 * 같은 일을 두 가지로 배우게 돼 있었다. 이제 양쪽 다 — 조건 3행(개념 · 라벨 · 문제 ID/작성자)
 * 옆에 [초기화]/[검색]이 세로로 서서 3행을 걸치고, 그 아래 난이도·정답·풀이·분석 필요만이
 * 한 행에 선다.
 * 접힌 필터가 결과를 조용히 좁히는 것을 막으려고 두었던 **조건 요약 칩도 함께 걷었다** —
 * 다 펼쳐져 있으면 요약이 중복이다. 「아직 적용 안 됨」은 [검색] 버튼의 테두리가 대신 말한다.
 * 개념은 이름을 치는 칸이 아니라 **후보에서 골라 칩으로 쌓는 방식**이다(concept_q → concept_ids).
 * 이 화면에만 있는 것은 그대로 산다 — 제목 행의 [AI 문제 생성], AI 모아보기 세그먼트, 새로고침.
 *
 * 옛 [분석 대상] 패널을 여기로 흡수했다(fe-handover §D · reply-batch §3-2). 두 목록은 모집단이
 * 같고 필터만 달랐는데, '미처리/전체'라는 라벨이 그 뜻을 못 드러내 같은 화면 둘로 읽혔다.
 * 이제 축은 **"분석 필요만 보기"(needs_analysis = 개념 또는 난이도가 비어 있음)** 체크박스 하나다.
 * ⚠ analysis_status 는 그 필터를 켤 때만 서버가 조인해 준다 — 안 켜면 항상 null 이라 칩도 폴링도 없다.
 * ⚠ "비어 있음"의 뜻이 2026-08-26 에 넓어졌다(계약 §1-5): 개념이 **`concept_ids:[]` 빈 배열**이어도
 *   "개념 필요"로 센다. 이전에는 빈 배열을 "있음"으로 봐서, 분석 화면은 개념이 없다는데 이 목록은
 *   필터에도 배지에도 안 걸리는 어긋난 화면이 났다. 그래서 같은 은행에서 건수가 늘어 보일 수 있다.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RefreshCw, X, Sparkles, ListChecks } from 'lucide-react';
import { Button, AiRunModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import {
  searchProblems, getOriginDisplay, difficultyBadgeClass,
  getNeedBadges, getAnalysisStatusDisplay, ANALYSIS_POLLING_STATUSES,
  CONCEPT_ALL_UNAVAILABLE,
} from './problemApi';
import { requestAnalysis, ANALYSIS_POLL_MS, AUTH_REDIRECT } from './analysisApi';
import AnalysisTargetChecks from './AnalysisTargetChecks';
import { presenceFromNeeds, defaultAnalysisTargets, toAnalysisOptions, hasAnyTarget } from './analysisTargets';
import ProblemTable, { NeedBadges } from './ProblemTable';
// 개념 고르개와 v2 개념 사전 — 시험지 구성 모달(PaperComposeModal)과 **같은 부품**을 쓴다.
// 두 화면은 같은 엔드포인트(problem-search)를 치는 형제라, 검색 조작이 갈리면 같은 일을
// 두 가지로 배우게 된다(오너 지시 2026-08-29 「그대로 적용」). 폴더를 넘는 import 지만
// 이 방향의 선례가 이미 있다 — PaperComposeModal 이 이 폴더의 problemApi 를 쓴다.
// 문제 번호 — 표(ProblemTable)와 같은 정본 부품. 얼굴이 한 벌이어야 같은 목록으로 읽힌다
import ProblemIdLink from './ProblemIdLink';
import ConceptFilterInput from '../ExamPapersV2/ConceptFilterInput';
import ConceptMatchToggle, { ConceptAllHint } from '../ExamPapersV2/ConceptMatchToggle';
// 조판 본문 — 표의 「본문 요약」 칸이 평문 대신 **지면과 같은 렌더러가 만든 HTML** 을 그린다.
import { toBodyHtmlMap } from '../ExamPapersV2/lexicalHtml';
import ProblemBodyPreview, { LIST_BODY_MAX_SCALE } from '../ExamPapersV2/ProblemBodyPreview';
// 개념 배지 — 이 저장소가 개념을 그리는 정본 모양([순번][유형] + 이름). 좁은 자리라 compact.
import { ConceptBadge, matchConcept } from '../ExamPapersV2/conceptBadge';
import { LabelBadges } from '../ExamPapersV2/labelBadge';
// concept_names 가 세 형태(객체배열·맵·문자열배열)로 오는 것을 id+이름 쌍으로 흡수하는 정본
import { conceptPairsOf } from '../ExamPapersV2/paperStats';
import { fetchConceptDictV2 } from '../ExamPapersV2/conceptBadge';
// 페이지 이동은 저장소 공용 부품을 쓴다 — 번호 클릭·직접 입력(Enter)·앞뒤 말줄임이 이미 들어 있다.
// 스타일이 CSS 파일에 있어(클래스 기반) 쓰는 쪽이 함께 들여야 한다.
import { DesktopPagination } from '../common';
import '../../styles/desktop-expanded.css';

const DIFFICULTY_OPTS = [1, 2, 3, 4, 5];

// 화면 입력 → API 필터. 빈 값은 넣지 않는다(빈 문자열도 조건이 된다).
function buildFilters(form, page) {
  // withBody — 표의 「본문 요약」 칸에 조판 본문을 그리기 위한 것(계약 §9). 응답이 커지지만
  // 별도 왕복이 없다. **레이아웃과 무관하게 늘 켠다**: 카드↔표 전환은 문제를 고르고 닫는 것이라
  // 목록을 다시 부르지 않는데, 켜고 끄면 표로 돌아왔을 때 본문 없는 목록을 보게 된다.
  const f = { page, perPage: 20, withBody: true };
  // 계약에 있는 값은 true 뿐 — 끌 때는 파라미터를 아예 빼야 한다(problemApi 가 그렇게 막아 둔다)
  if (form.needsOnly) f.needsAnalysis = true;
  // 개념은 **이름이 아니라 id** 로 건다(계약 §3 — `concept_ids` 는 "하나라도 포함").
  // 이름 부분검색(concept_q)은 맞는 개념이 200개를 넘으면 400 이라 넓은 말이 통째로 막혔다.
  // AND(모두 포함)를 고르면 방식을 함께 싣는다 — 어느 검색으로 나갈지(둘 이상이면 본문 검색)는 problemApi 한 곳이 정한다.
  if (form.conceptIds.length > 0) {
    f.conceptIds = form.conceptIds.join(',');
    if (form.conceptMatch === 'all') f.conceptMatch = 'all';
  }
  if (form.labels.trim()) f.labels = form.labels.split(',').map((s) => s.trim()).filter(Boolean).join(',');
  if (form.difficulty) f.difficulty = form.difficulty;
  if (form.author.trim()) f.author = form.author.trim();
  if (form.problemIds.trim()) f.problemIds = form.problemIds.replace(/\s/g, '');
  if (form.hasAnswer !== '') f.hasAnswer = form.hasAnswer === 'yes';
  if (form.hasSolution !== '') f.hasSolution = form.hasSolution === 'yes';
  if (form.aiFilter === 'ai') f.originType = 200;
  else if (form.aiFilter === 'generated') f.aiKind = 'generated';
  else if (form.aiFilter === 'variation') f.aiKind = 'variation';
  return f;
}

/**
 * 목록 조회 실패를 사람 말로 옮긴다 — 갈림은 **상태코드**다(계약 §1-5·§3).
 * problemApi 의 getJsonWithStatus 가 Error.httpStatus 와 서버 message 를 살려 준다.
 *
 * 400 의 갈래 하나가 2026-08-29 로 **사라졌다**: 종전에는 `concept_q`(개념명 부분검색)에 맞는
 * 개념이 200개를 넘으면 400 이라, 넓은 말을 친 경우를 짚어 주는 안내가 붙어 있었다. 개념을
 * 이름이 아니라 **id 로 골라 걸게 되면서**(concept_ids) 그 벽 자체가 없어져 안내도 걷었다.
 * 남은 400 은 문제 ID 형식 같은 것이라 서버 message 를 그대로 옮긴다.
 */
/**
 * 검색 응답에 실려 온 body_lexical → HTML 지도. **왕복하지 않는다** — 재료는 방금 받은
 * 응답이다(with_body=1, 계약 §9). 본문이 없는 항목은 키를 넣지 않는다 → 그 칸이 평문으로 남는다.
 * (PaperComposeModal 에 같은 것이 있다. 세 번째가 생기면 lexicalHtml 로 뽑는다 —
 *  그 파일 자체가 「세 번째로 같은 것을 필요로 하면서」 뽑혀 나온 자리다.)
 */
function toSearchBodyMap(items) {
  const docs = {};
  (Array.isArray(items) ? items : []).forEach((it) => {
    const pid = it?.problem_id ?? it?.id;
    if (pid != null && it?.body_lexical != null) docs[String(pid)] = it.body_lexical;
  });
  return toBodyHtmlMap(docs);
}

function searchErrorText(e) {
  if (e?.httpStatus === 400) return e.message || '검색 조건을 서버가 받지 못했습니다.';
  // 403 은 401 과 다르다 — 401 은 다시 로그인하면 풀리고, 403 은 계정 자체에 권한이 없다.
  // 뭉뚱그려 "다시 시도"라고 안내하면 사용자가 같은 벽에 계속 부딪힌다.
  // 어느 등급이 필요한지는 적지 않는다 — 계약 §3 이 못박은 것은 `/api/v2/problems` 목록의
  // manager_only 이고, 이 검색 경로의 등급까지 같다고 확인된 것은 아니다(불확실).
  if (e?.httpStatus === 403) {
    return '이 목록을 볼 권한이 없습니다 — 계정에 권한이 없어 다시 로그인해도 풀리지 않습니다. 관리자에게 문의하세요.';
  }
  return e?.message || '문제 목록을 불러오지 못했습니다.';
}

const EMPTY_FORM = {
  // 개념은 칩으로 고른 id 배열이다. 종전의 conceptQ(이름 부분검색) + conceptId(조회 탭에서
  // 넘어온 정확 검색) 두 칸이 여기 하나로 합쳐졌다 — 둘 다 결국 「어느 개념인가」였고,
  // 이름으로 고르든 다른 탭에서 눌러 오든 화면에 남는 모습은 같은 칩이다.
  conceptIds: [],
  conceptMatch: 'any', // 개념 칸 안쪽 OR/AND — 'any'(하나라도) | 'all'(모두). [초기화]가 OR 로 되돌린다
  labels: '', difficulty: '', author: '',
  problemIds: '', hasAnswer: '', hasSolution: '', aiFilter: '',
  needsOnly: false, // needs_analysis — 개념 또는 난이도가 비어 있는 문제만
};

/**
 * 걸린 조건 — **화면에 그리지 않는다.** 조건이 전부 펼쳐진 뒤로는(2026-08-29) 요약이 중복이라
 * 칩 줄을 걷었다. 남은 쓸모는 빈 결과 안내가 「조건 때문에 없는가, 원래 없는가」를 가르는 것뿐이다.
 */
function activeChips(form) {
  const chips = [];
  if (form.conceptIds.length > 0) chips.push(['conceptIds', `개념 ${form.conceptIds.length}개`]);
  if (form.labels.trim()) chips.push(['labels', `라벨 ${form.labels.trim()}`]);
  if (form.difficulty) chips.push(['difficulty', `난이도 ${form.difficulty}`]);
  if (form.author.trim()) chips.push(['author', `작성자 ${form.author.trim()}`]);
  if (form.problemIds.trim()) chips.push(['problemIds', `ID ${form.problemIds.trim()}`]);
  if (form.hasAnswer !== '') chips.push(['hasAnswer', form.hasAnswer === 'yes' ? '정답 있음' : '정답 없음']);
  if (form.hasSolution !== '') chips.push(['hasSolution', form.hasSolution === 'yes' ? '풀이 있음' : '풀이 없음']);
  if (form.aiFilter === 'ai') chips.push(['aiFilter', 'AI 문제']);
  else if (form.aiFilter === 'generated') chips.push(['aiFilter', 'AI 생성']);
  else if (form.aiFilter === 'variation') chips.push(['aiFilter', 'AI 변형']);
  if (form.needsOnly) chips.push(['needsOnly', '분석 필요만(개념·난이도 없음)']);
  return chips;
}

function ProblemSearchPanel({
  selectedId, onSelect, reloadKey = 0, preset = null,
  layout = 'card', // 'table' 넓은 폭(문제관리와 같은 표) | 'card' 좁은 마스터. 담는 쪽이 정한다.
  onPreview = null, // 주면 표에 [미리보기] 행 액션이 생긴다(본문을 수식까지 렌더해 보는 자리)
  onSingleOcr = null,
  onGenerate = null, // 주면 제목 행에 [AI 문제 생성]이 선다. 모달은 담는 쪽이 연다.
  onOpenAnalysisJobs = null, // 주면 제목 행에 [분석 작업]이 선다. 모달은 담는 쪽이 연다.
  /**
   * 행마다 붙는 [분석]([AI 분석 요청])을 세울지 — **LLM 비용**이 나는 실행이라 화면에서만 감춘다
   * (서버는 교사에게 열려 있다: BE-20260910 회신 §1-1·§5-①).
   *
   * 위 두 진입점과 달리 boolean 인 이유: 이 버튼은 담는 쪽이 준 콜백이 아니라 **패널 제 상태**
   * (aiTarget)로 창을 여니 콜백을 빼는 방식으로는 가릴 수가 없다.
   * 기본값이 허용(true)인 이유: prop 을 안 넘기는 마운트 지점·시험에서 버튼이 말없이 사라지면
   * 검사하던 것이 없어졌는데도 시험이 통과한다. 판정은 담는 쪽(pages/ProblemHub.js)이 내려 준다.
   */
  canRunAi = true,
}) {
  const { getAccessToken } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);      // 입력 중인 값
  const [applied, setApplied] = useState(EMPTY_FORM); // 실제 조회에 쓴 값
  // 개념 사전. null 은 「아직 없음/못 받음」이고 화면이 그렇게 말한다.
  const [conceptDict, setConceptDict] = useState(null);
  const [dictLoading, setDictLoading] = useState(false);
  const [dictKey, setDictKey] = useState(0); // 다시 시도용
  const [rows, setRows] = useState({ items: [], page: 1, totalPages: 1, totalItems: 0 });
  // problem_id → 조판 본문 HTML. 목록과 짝으로 갈린다(옛 본문이 새 목록에 남지 않게).
  const [bodies, setBodies] = useState({});
  const [loading, setLoading] = useState(true);
  // { text, action } — action 은 'retry'(일시 실패) | 'reset'(조건을 고쳐야 풀림) | 'none'(권한)
  //   | 'or'(이 서버에 AND 검색이 아직 없음 — 같은 조건을 OR 로 다시).
  // 같은 조건으로 다시 눌러 봐야 같은 벽인 실패에 [다시 시도]를 내밀지 않는다. 눌러도 되는
  // 버튼처럼 생긴 버튼이 아무것도 바꾸지 못하는 것이 이런 화면에서 가장 흔한 거짓말이다.
  const [error, setError] = useState(null);
  const [analyzingIds, setAnalyzingIds] = useState(() => new Set()); // 요청 왕복 중 — 이중 요청 금지
  // AI 요청 창의 대상 — 목록은 행마다 문제가 달라 「무엇을 분석하는지」를 창이 알아야 한다.
  // null 이면 닫힘. 표의 [분석]과 아래 안내의 [분석]이 같은 창을 연다.
  const [aiTarget, setAiTarget] = useState(null);
  // 그 창의 「이번에 만들 것」 체크 넷 — 열 때마다 그 행의 need_* 로 다시 세운다(openAiWindow).
  const [aiPicks, setAiPicks] = useState(() => defaultAnalysisTargets(null));
  const [notice, setNotice] = useState(null); // 분석 요청 결과 한 줄(라이브 리전)
  const seqRef = useRef(0);
  const pageRef = useRef(1); // 승인 등 외부 갱신에서 현재 페이지를 지키기 위한 최신값
  const appliedRef = useRef(EMPTY_FORM); // 폴링·비동기 완료가 "지금 무슨 조건인지" 되물을 자리
  const inFlightRef = useRef(new Set());  // 같은 프레임 연타 방지(상태보다 한 박자 빠르다)
  const aliveRef = useRef(true);
  // StrictMode 는 mount→cleanup→mount 다 — 본문에서 되살리지 않으면 두 번째 마운트가 죽은 채로 산다
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  // silent=true 는 폴링용 — 스피너를 돌리지 않고(목록이 6초마다 깜빡이면 못 읽는다) 실패해도
  // 화면을 비우지 않는다. 폴링 한 번 실패했다고 보던 목록이 오류 화면으로 바뀌면 안 된다.
  const load = useCallback(async (nextForm, page, { silent = false } = {}) => {
    const seq = (seqRef.current += 1);
    appliedRef.current = nextForm;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const d = await searchProblems(buildFilters(nextForm, page), getAccessToken);
      if (seq !== seqRef.current) return; // 빠른 재검색 — 늦게 온 응답이 최신 결과를 덮지 않게
      setRows(d);
      pageRef.current = d.page ?? page;
      // 본문은 **같은 응답에 실려 왔다**(with_body=1) — 목록을 먼저 세우고 본문만 뒤이어 채운다.
      // 변환이 넘어져도 검색 자체는 성공이라 바깥 try 와 따로 가둔다: 여기서 던지면 목록이 통째로
      // 오류 화면으로 뒤집힌다. 변환기를 못 받으면 전 칸이 평문 요약으로 남을 뿐이다.
      let map = {};
      try {
        map = await toSearchBodyMap(d.items);
      } catch (err) {
        console.error('검색 결과 본문 변환기 로드 실패:', err);
      }
      // 변환을 기다리는 동안 다음 검색이 시작됐을 수 있다 — 늦은 판의 본문으로 덮지 않는다
      if (seq !== seqRef.current) return;
      setBodies(map);
    } catch (e) {
      if (seq !== seqRef.current || silent) return;
      const status = e?.httpStatus;
      // 'or' — 이 서버에 AND(모두 포함) 검색이 아직 없다(본문 검색 배포 전). 조건이 틀린 게 아니니 지우라고 하지 않고
      // OR 로 다시 찾는 길을 준다(오너 결정 D4). 408(본문 검색의 5초 상한)은 같은 조건이면 같은 벽이라 400 과 같은 갈래다.
      setError({
        text: searchErrorText(e),
        action: e?.reason === CONCEPT_ALL_UNAVAILABLE ? 'or'
          : (status === 400 || status === 408) ? 'reset'
            : status === 403 ? 'none' : 'retry',
      });
      setRows({ items: [], page: 1, totalPages: 1, totalItems: 0 });
      setBodies({}); // 결과와 짝으로 비운다 — 본문만 남으면 옛 목록의 본문을 들고 있게 된다
    } finally {
      // 플래그는 이 호출의 것이다 — 폴링 tick 이 화면 조회의 순번을 무효화했다고 스피너를
      // 영원히 남기면 안 된다(폴링과 화면 조회가 같은 seqRef 를 쓴다).
      if (!silent) setLoading(false);
    }
  }, [getAccessToken]);

  // 개념 사전은 한 번만. fetchConceptDictV2 가 5분 모듈 캐시라, 시험지 구성 모달을 먼저 열어
  // 두었다면 여기서는 왕복이 아예 없다(두 화면이 같은 사전을 나눠 쓴다).
  useEffect(() => {
    let alive = true;
    setDictLoading(true);
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((d) => { if (alive) setConceptDict(d); })
      .finally(() => { if (alive) setDictLoading(false); });
    return () => { alive = false; };
  }, [getAccessToken, dictKey]);

  // 최초 1회 + 외부 갱신 신호(승인·생성 등으로 목록이 낡을 때).
  // 페이지를 1로 되돌리지 않는다 — 3페이지에서 승인하고 목록이 1페이지로 튀면 자리를 잃는다.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도: 외부 신호에만 반응(입력 중 재조회 금지)
  useEffect(() => { load(applied, pageRef.current); }, [reloadKey]);

  const runSearch = (nextForm = form) => {
    setApplied(nextForm);
    load(nextForm, 1);
  };

  // 밖에서 개념을 지정해 이 목록을 좁히는 진입로.
  // ⚠ **2026-08-29 현재 이것을 주는 곳이 없다.** 종전에는 조회 탭의 개념 배지가 눌리면
  //   여기로 왔는데, 그 배지가 「설명 보기」로 뜻이 바뀌면서(오너 지시) 진입점이 사라졌다.
  //   길 자체는 멀쩡하니 남겨 둔다 — 「어느 개념의 문제만 보기」를 다시 붙일 때 이 자리다.
  // preset 은 누를 때마다 새 객체라, 같은 개념을 두 번 눌러도 다시 걸린다.
  const presetRef = useRef(null);
  useEffect(() => {
    if (!preset || preset === presetRef.current) return;
    presetRef.current = preset;
    const next = { ...EMPTY_FORM, conceptIds: [Number(preset.conceptId)] };
    setForm(next);
    setApplied(next);
    load(next, 1);
  }, [preset, load]);

  const resetAll = () => {
    setForm(EMPTY_FORM);
    runSearch(EMPTY_FORM);
  };

  // 이 체크박스만 즉시 적용한다(AI 모아보기와 같은 결). 목록의 분석 상태·폴링이 이 값에 매달려
  // 있어서, '적용 대기'로 두면 칩은 켜졌는데 상태 열은 안 뜨는 어긋난 화면이 된다.
  const toggleNeedsOnly = (v) => {
    const next = { ...applied, needsOnly: v };
    setForm((f) => ({ ...f, needsOnly: v }));
    setApplied(next);
    load(next, 1);
  };

  // [OR로 다시 검색] — 이 서버에 AND(모두 포함) 검색이 아직 없을 때(본문 검색 배포 전) 오류 자리에만 선다.
  // 걸었던 조건은 그대로 두고 방식만 OR 로 바꿔 곧바로 찾는다. 입력칸과 적용값을 함께 바꾸는 것은
  // 위와 같은 이유다 — 한쪽만 바꾸면 [검색]에 「적용 안 됨」 테두리가 남는다.
  const searchWithOr = () => {
    const next = { ...applied, conceptMatch: 'any' };
    setForm((f) => ({ ...f, conceptMatch: 'any' }));
    setApplied(next);
    load(next, 1);
  };

  // ── 분석 상태 폴링 — needs_analysis 를 켰을 때만 ──
  // 안 켜면 analysis_status 는 조인 skip 이라 항상 null 이다(reply-batch §3-1). 진행 중인 것이
  // 하나도 없으면 돌 이유도 없다 — 200/500 이 되는 순간 조건이 꺼지며 스스로 멈춘다.
  const polling = applied.needsOnly
    && rows.items.some((it) => ANALYSIS_POLLING_STATUSES.includes(it.analysis_status));
  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(() => { load(appliedRef.current, pageRef.current, { silent: true }); }, ANALYSIS_POLL_MS);
    return () => clearInterval(t);
  }, [polling, load]);

  // 표의 [분석]과 카드 목록의 [분석]이 같은 창을 연다. 체크는 열 때마다 그 행 기준으로 다시 세운다 —
  // 앞 행에서 켠 「정답 다시 만들기」가 정답이 있는 다음 행에 조용히 실리지 않게.
  const openAiWindow = (item) => {
    setAiPicks(defaultAnalysisTargets(presenceFromNeeds(item)));
    setAiTarget(item);
  };

  // ── 분석 요청 — 창에서 고른 것을 AI가 만든다(analysis-review-screen §2-2) ──
  // options 는 넷을 늘 true/false 로 싣는다 — 생략하면 서버가 「은행에 있으면」 조용히 건너뛴다(analysisTargets).
  const askAnalysis = async (item, userPrompt, options) => {
    const id = item.problem_id;
    // 상태(analyzingIds)는 다음 렌더에야 버튼을 잠근다 — 같은 프레임의 두 번째 클릭은 ref 로 막는다
    if (inFlightRef.current.has(id)) return;
    inFlightRef.current.add(id);
    setAnalyzingIds((s) => new Set(s).add(id));
    setNotice(null);
    try {
      const d = await requestAnalysis(id, { options, userPrompt }, getAccessToken);
      if (d === AUTH_REDIRECT) return; // 로그인으로 튕겼다 — 성공했다고 말하지 않는다
      if (!aliveRef.current) return;
      // 낙관적 '대기' — 서버가 상태를 실어 주는 건 needs_analysis 를 켰을 때뿐이다. 안 켰는데
      // 칩을 만들어 두면 그 행만 상태가 있는 척하고, 폴링도 그 거짓 값을 좇는다.
      if (appliedRef.current.needsOnly) {
        setRows((r) => ({
          ...r,
          items: r.items.map((it) => (it.problem_id === id
            ? { ...it, analysis_status: ANALYSIS_POLLING_STATUSES.includes(it.analysis_status) ? it.analysis_status : 100 }
            : it)),
        }));
      }
      // dedup:'already' = 새 요청이 서지 않고 **진행 중이던 행이 그대로 끝난다**. 그러니 이번에 쓴
      // 지시도, 고른 항목도 어디에도 반영되지 않는다(BE-20260829-reply-fe-0828.md §4-1이 명시).
      // 지시는 실제로 쓴 사람에게만 말한다 — 안 쓴 사람에게는 없는 손실을 알리는 소음이다.
      // 고른 항목은 늘 실어 보내므로(진행 중인 분석과 다를 수 있다) 늘 말한다.
      const wrotePrompt = !!(userPrompt && userPrompt.trim());
      setNotice({
        tone: 'info',
        text: d?.dedup === 'already'
          ? `#${id} 이미 분석이 진행 중입니다 — 이어서 기다립니다. 이번에 고른 항목${wrotePrompt ? '과 쓴 지시는' : '은'} 반영되지 않습니다(진행 중인 분석이 그대로 끝납니다).`
          : `#${id} 분석을 요청했습니다.`,
      });
    } catch (e) {
      if (!aliveRef.current) return;
      setNotice({
        tone: 'error',
        text: e.httpStatus === 409
          ? `#${id} 본문이 확정되지 않은 문제입니다 — 본문 확정 후 분석 가능합니다.`
          : `#${id} ${e.message || '분석 요청에 실패했습니다.'}`,
      });
    } finally {
      inFlightRef.current.delete(id);
      if (aliveRef.current) {
        setAnalyzingIds((s) => { const n = new Set(s); n.delete(id); return n; });
      }
    }
  };

  const chips = useMemo(() => activeChips(form), [form]);
  const dirty = JSON.stringify(form) !== JSON.stringify(applied);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-3xl bg-white p-3 shadow-lg">
      {/* 검색바. relative z-10 — 개념 후보 목록이 이 묶음 안에서 아래로 뜨는데, 결과 목록은
          다음 형제라 나중에 그려진다. 층을 안 주면 후보가 목록 카드 뒤로 들어간다. */}
      <div className="relative z-10 shrink-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="shrink-0 text-sm font-bold text-gray-800">문제</h2>
          {/* 문제와 무관한 별도 진입 둘 — 오른쪽에 나란히 세운다.
              · [분석 작업] : 태운 분석을 상태별로 모아 보고 **실패한 것을 다시 태우는** 자리
                (BE-20260829-reply-fe-0828.md §1-2). 이 목록은 **문제**를 세고 저쪽은 **작업**을 센다.
                AI 동작이 아니라 목록 열기라 회색이다 — 보라는 이 화면에서 AI 요청의 색이다.
              · [AI 문제 생성] : 개념·지시로 무에서 새 문제를 만든다(계약 §8).
              둘 다 결과가 쌓이는 곳이 이 목록이라 목록의 머리가 제자리다. */}
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
            {onOpenAnalysisJobs && (
              <button
                type="button"
                onClick={onOpenAnalysisJobs}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
              >
                <ListChecks className="size-3.5" />
                분석 작업
              </button>
            )}
            {onSingleOcr && <button type="button" onClick={onSingleOcr} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">한 문제 OCR</button>}
            {onGenerate && (
              <button
                type="button"
                onClick={onGenerate}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1.5 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-200"
              >
                <Sparkles className="size-3.5" />
                AI 문제 생성
              </button>
            )}
          </div>
        </div>

        {/* 검색 조건 — **시험지 구성 모달(PaperComposeModal)과 같은 배치다**(오너 지시 2026-08-29
            「그대로 적용」). 두 화면은 같은 엔드포인트를 치는 형제인데 조작이 갈려 있었다.
            · 조건 3행(개념 · 라벨 · 문제 ID/작성자)을 한 묶음으로 싸고 [초기화]/[검색] 기둥이
              그 옆에 세로로 서서 3행을 걸친다.
            · **상세 조건 접기를 걷었다.** 접힌 필터가 결과를 조용히 좁히는 것을 막으려고 요약 칩을
              두었던 것인데, 다 펼치면 그 장치 자체가 필요 없다 — 칩 줄도 함께 걷었다.
            · 이 묶음이 아래 두 줄(④ 필터 · AI 모아보기)보다 위 층에 선다. 개념 후보 목록이
              ①행 안에서 층을 갖는데 그 층은 이 묶음 안에서만 통해서, 올리지 않으면 후보가
              난이도·정답·풀이 줄 뒤로 들어간다(같은 자로 재서 확인).
            · 개념 칸의 안내 글만 모달과 다르다 — 이 열은 문제를 고르면 좁은 마스터(안쪽 312px)로
              접히는데 거기서 글자 자리가 130.9px 뿐이라, 모달의 긴 문구(163.3px)가 잘린다.
              종전 이 화면이 쓰던 짧은 문구를 그대로 둔다. */}
        <div className="relative z-20 flex gap-1.5">
          <div className="min-w-0 flex-1 space-y-2">
            <ConceptFilterInput
              value={form.conceptIds}
              onChange={(ids) => setForm({ ...form, conceptIds: ids })}
              dict={conceptDict}
              loading={dictLoading}
              onRetry={() => setDictKey((k) => k + 1)}
              onSubmit={() => runSearch()}
              placeholder="개념명으로 검색"
              /* OR/AND — 조립 창과 같은 부품. 바꿔도 곧바로 찾지 않는다(개념 칩과 같은 결 — [검색]으로 건다) */
              trailing={(
                <ConceptMatchToggle
                  value={form.conceptMatch}
                  onChange={(m) => setForm({ ...form, conceptMatch: m })}
                />
              )}
            />
            <ConceptAllHint match={form.conceptMatch} count={form.conceptIds.length} />
            <input
              type="text"
              value={form.labels}
              onChange={(e) => setForm({ ...form, labels: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
              aria-label="라벨"
              placeholder="라벨 — 콤마로 여러 개(모두 포함)"
              className="w-full rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
            />
            <div className="flex gap-1.5">
              <input
                type="text"
                value={form.problemIds}
                onChange={(e) => setForm({ ...form, problemIds: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
                aria-label="문제 ID"
                placeholder="문제 ID — 콤마로 여러 개"
                className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
              />
              <input
                type="text"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
                aria-label="작성자"
                placeholder="작성자"
                className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-1.5">
            <Button size="sm" variant="secondary" className="flex-1" onClick={resetAll}>초기화</Button>
            {/* 고친 조건이 아직 안 걸렸다는 신호를 **이 버튼이 낸다**. 종전에는 아래 줄 오른쪽
                끝에 「Enter 또는 [검색]으로 적용」이라고 적었는데, 그 자리를 건수에 내주었다.
                자리를 더 먹지 않으면서 신호가 해결책 위에 선다 — 누르면 사라지는 표시다. */}
            <Button
              size="sm"
              className={`flex-1 ${dirty ? 'ring-2 ring-amber-400' : ''}`}
              title={dirty ? '고친 조건이 아직 적용되지 않았습니다 — 눌러서 적용' : undefined}
              onClick={() => runSearch()}
              disabled={loading}
            >
              검색
            </Button>
          </div>
        </div>

        {/* 난이도·정답·풀이·분석 필요만 한 행. 난이도 옵션에서 「(보통)」 같은 뜻말을 뺀 이유는
            폭이다 — select 는 닫혀 있어도 가장 긴 옵션에 맞춰 벌어져, 뜻말을 두면 이 좁은 마스터에
            다섯이 한 행에 못 선다. 오른쪽 끝은 **건수가 아니라 「적용 안 됨」 안내**다 —
            건수는 이 화면에서는 제목 행에 이미 있다(role=status 로 읽어 주는 자리도 거기다). */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <label className="flex items-center gap-1 text-[11px] text-gray-500">
            난이도
            <select
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
            >
              <option value="">전체</option>
              {DIFFICULTY_OPTS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1 text-[11px] text-gray-500">
            정답
            <select
              value={form.hasAnswer}
              onChange={(e) => setForm({ ...form, hasAnswer: e.target.value })}
              className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
            >
              <option value="">전체</option>
              <option value="yes">있음</option>
              <option value="no">없음</option>
            </select>
          </label>
          <label className="flex items-center gap-1 text-[11px] text-gray-500">
            풀이
            <select
              value={form.hasSolution}
              onChange={(e) => setForm({ ...form, hasSolution: e.target.value })}
              className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
            >
              <option value="">전체</option>
              <option value="yes">있음</option>
              <option value="no">없음</option>
            </select>
          </label>
          {/* 이 체크박스만 즉시 적용한다(AI 모아보기와 같은 결) — 목록의 분석 상태·폴링이 이 값에
              매달려 있어서, '적용 대기'로 두면 켜졌는데 상태 열은 안 뜨는 어긋난 화면이 된다. */}
          <label
            title="개념·난이도가 비어 있는 문제만 봅니다. 켜면 각 문제의 분석 진행 상태(대기·분석 중·완료)도 함께 보입니다."
            className="flex cursor-pointer items-center gap-1 text-[11px] text-gray-600"
          >
            <input
              type="checkbox"
              checked={form.needsOnly}
              onChange={(e) => toggleNeedsOnly(e.target.checked)}
              className="size-3.5 shrink-0 accent-primary"
            />
            분석 필요만
          </label>
          {/* 결과 건수 — 조건 바로 밑 오른쪽 끝(모달과 같은 자리·같은 규격). 조건을 좁힌 것이
              얼마나 먹었는지 눈이 바로 옆에서 확인한다. role=status 는 제목 행에서 이 자리로
              따라왔다 — 값이 사는 곳과 읽어 주는 곳이 갈리면 안 된다. */}
          <span role="status" aria-live="polite" className="ml-auto text-[11px] text-gray-400">
            {loading ? '읽는 중…' : `${rows.totalItems}건`}
          </span>
        </div>

        {/* AI 모아보기 — 계약 §2가 별도 배포한 축이라 접지 않고 바로 노출.
            오른쪽 끝은 [새로고침] 자리다(제목 행을 [AI 문제 생성]에 내줬다). 새로고침은 모아보기
            필터가 아니므로 role="group" 밖에 세운다 — 안에 넣으면 다섯 번째 필터로 읽힌다. */}
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1 text-[11px]" role="group" aria-label="AI 문제 모아보기">
            <Sparkles aria-hidden="true" className="size-3 shrink-0 text-violet-400" />
            {[['', '전체'], ['ai', 'AI 전체'], ['generated', 'AI 생성'], ['variation', 'AI 변형']].map(([v, label]) => (
              <button
                key={v || 'all'}
                type="button"
                onClick={() => {
                  const nextApplied = { ...applied, aiFilter: v };
                  setForm((f) => ({ ...f, aiFilter: v }));
                  setApplied(nextApplied);
                  load(nextApplied, 1);
                }}
                aria-pressed={applied.aiFilter === v}
                className={`rounded-full px-2 py-0.5 transition-colors ${
                  applied.aiFilter === v ? 'bg-violet-100 font-medium text-violet-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load(applied, rows.page)}
            disabled={loading}
            title="새로고침"
            aria-label="목록 새로고침"
            className="ml-auto shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* 분석 요청 결과 — 리전은 늘 마운트해 두고 비었을 때만 감춘다(껐다 켜면 못 읽어 준다) */}
        <p
          role="status"
          aria-live="polite"
          className={notice
            ? `flex items-start gap-1 text-[11px] ${notice.tone === 'error' ? 'text-danger' : 'text-gray-500'}`
            : 'sr-only'}
        >
          {notice?.text || ''}
          {notice && (
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="알림 닫기"
              className="ml-auto shrink-0 text-gray-400 hover:text-gray-600"
            >
              <X className="size-3" />
            </button>
          )}
        </p>
      </div>

      {/* 결과 — min-w-0 이 없으면 표의 최소 폭이 패널을 밀어 가로 스크롤이 표 밖으로 새어 나간다 */}
      <div className="mt-2 min-h-0 min-w-0 flex-1 overflow-y-auto">
        {error && (
          <div className="space-y-2 p-3">
            <p className="text-sm text-danger">{error.text}</p>
            {error.action === 'retry' && (
              <Button size="xs" variant="secondary" onClick={() => load(applied, rows.page)}>다시 시도</Button>
            )}
            {/* 조건을 고쳐야 풀리는 실패(400)에는 조건을 비우는 손잡이를 준다 —
                개념명이 너무 짧아 막힌 경우가 대부분이라 이 한 번으로 목록이 돌아온다. */}
            {error.action === 'reset' && (
              <Button size="xs" variant="secondary" onClick={resetAll}>조건 모두 지우기</Button>
            )}
            {/* AND 검색이 없는 서버(본문 검색 배포 전) — 조건은 그대로, 방식만 OR 로 다시 찾는다(오너 결정 D4) */}
            {error.action === 'or' && (
              <Button size="xs" variant="secondary" onClick={searchWithOr}>OR로 다시 검색</Button>
            )}
            {/* 403 에는 버튼을 두지 않는다 — 이 화면에서 사용자가 할 수 있는 일이 없다.
                무엇을 눌러도 같은 벽이면, 버튼이 없는 편이 정직하다. */}
          </div>
        )}
        {!error && loading && rows.items.length === 0 && (
          <div className="space-y-2 p-2">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
          </div>
        )}
        {!error && !loading && rows.items.length === 0 && (
          <p className="p-6 text-center text-sm text-gray-400">
            {applied.needsOnly && chips.length === 1
              ? '개념·난이도가 비어 있는 문제가 없습니다 — 분석할 것이 남아 있지 않습니다.'
              : chips.length > 0 ? '조건에 맞는 문제가 없습니다 — 조건을 줄여 보세요.' : '문제가 없습니다.'}
          </p>
        )}
        {/* 넓은 폭은 표(문제관리와 같은 감각), 좁은 마스터는 카드. 10컬럼은 좁은 폭에 안 들어간다. */}
        {layout === 'table' ? (
          <ProblemTable
            items={rows.items}
            bodies={bodies}
            dict={conceptDict}
            selectedId={selectedId}
            onSelect={onSelect}
            onPreview={onPreview}
            /* 표 쪽은 콜백이 없으면 행 액션을 안 그린다 — 그 부품은 손대지 않고 여기서 가른다 */
            onRequestAnalysis={canRunAi ? openAiWindow : null}
            analyzingIds={analyzingIds}
          />
        ) : (
          /* 카드 목록 — 문제를 열어 마스터가 좁아졌을 때의 얼굴. 간격 4px → 8px(space-y-2).
             본문을 자르지 않으므로(clamp=false) 카드 높이가 제각각인데, 종전에는 테두리가
             border-transparent 여서 **경계가 아예 없었다** — 한 문제의 라벨 줄과 다음 문제의
             번호 줄이 4px 거리에 나란히 놓여 어디서 문제가 갈리는지 눈으로 찾을 수 없었다.
             (오너 지시 2026-08-31) */
          <ul className="space-y-2">
            {rows.items.map((it) => {
              const active = String(it.problem_id) === String(selectedId);
              const origin = getOriginDisplay(it);
              const needs = getNeedBadges(it);
              const st = it.analysis_status == null ? null : getAnalysisStatusDisplay(it.analysis_status);
              const inFlight = analyzingIds.has(it.problem_id);
              const inProgress = ANALYSIS_POLLING_STATUSES.includes(it.analysis_status);
              return (
                <li key={it.problem_id}>
                  {/* 카드 안에 버튼을 하나 더 두려면 카드 자체는 버튼이 아니어야 한다(중첩 금지) */}
                  {/* 테두리·바탕·그림자는 이 저장소의 카드 정본과 같은 값을 쓴다
                      (StudentsHub/HubReportCard: rounded border-gray-200 bg-white shadow-sm).
                      여기서만 다른 회색을 고르면 같은 콘솔 안에서 카드가 두 얼굴이 된다.
                      선택된 카드는 종전대로 브랜드색 테두리와 옅은 바탕으로 선다. */}
                  <div
                    className={`flex items-start gap-1 rounded-xl border shadow-sm transition-colors ${
                      active
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-gray-200 bg-white hover:border-primary/30 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(it.problem_id)}
                      aria-current={active ? 'true' : undefined}
                      className="min-w-0 flex-1 p-2 text-left"
                    >
                      <div className="flex items-center gap-1.5">
                        {/* 번호가 곧 상태다 — 표와 **같은 부품**을 쓴다(ProblemIdLink).
                            크기도 표와 같은 12px 로 올렸다(오너 지시 2026-09-01 「기준은 표」).
                            종전 근거였던 「형제 배지가 9~10px 이라 위계가 뒤집힌다」는 뒤집어 본다 —
                            번호는 배지가 아니라 이 카드의 **이름**이라 배지보다 커야 맞다.
                            **링크로 만들지 않는다** — 카드 전체가 이미 이 문제를 여는 button 이라
                            안에 anchor 를 넣으면 버튼 안의 버튼이 되고, 가는 곳도 똑같다. */}
                        <ProblemIdLink id={it.problem_id} item={it} linked={false} className="shrink-0" />
                        {origin && (
                          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${origin.cls}`}>{origin.text}</span>
                        )}
                        {it.difficulty != null && (
                          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(it.difficulty)}`}>난이도 {it.difficulty}</span>
                        )}
                        {/* 값이 있을 때만 — null 은 '분석 전'이 아니라 '조인 안 함'일 수 있다 */}
                        {st && (
                          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${st.cls}`}>{st.text}</span>
                        )}
                        {it.author && <span className="ml-auto min-w-0 truncate text-[10px] text-gray-400">{it.author}</span>}
                      </div>
                      {/* 개념 — **본문 바로 위**다(오너 지시 2026-08-29). 무슨 문제인지 읽기 전에
                          어느 개념인지 먼저 눈에 들어오는 순서다. 모양은 이 저장소의 개념 배지
                          그대로 쓰되 좁은 자리라 compact(한 단 작은 [순번][유형] + 이름).
                          **누를 수 없다** — 카드 전체가 이미 button 이라 안에 버튼을 두면 중첩이 된다.
                          순번·유형은 개념 사전에서 채우고, 못 받았으면 이름만 남는다. */}
                      {(() => {
                        const pairs = conceptPairsOf(it);
                        if (pairs.length === 0) return null;
                        return (
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            {pairs.map((c, i) => {
                              const mc = matchConcept(conceptDict, c.id);
                              return (
                                <ConceptBadge
                                  key={c.id ?? `${c.name}-${i}`}
                                  name={c.name}
                                  order={mc?.order ?? null}
                                  type={mc?.concept_type ?? null}
                                  compact
                                />
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* 라벨 — **개념 바로 아래, 본문 위**다(오너 지시 2026-09-02 · 조립 창의 검색
                          카드(PaperComposeModal)와 같은 자리·같은 간격 mt-0.5). 배지는 표·상세·조립 창과
                          **같은 부품**(ExamPapersV2/labelBadge)이다.
                          본문 아래에 있던 동안에는 문제를 고르는 단서 둘(개념·라벨)이 본문을 사이에
                          두고 갈라져, 카드 하나를 위아래로 두 번 훑어야 했다.
                          꼬리표를 **자르지 않는다**(오너 지시 「생략하지 말고 모두」) — 종전에는 라벨과
                          개념을 ` · ` 로 이어 붙인 한 줄을 truncate 로 잘라, 값이 여럿인데 한 문장처럼
                          보였고 뒤쪽 값은 화면에 아예 없었다. */}
                      <LabelBadges labels={it.labels} className="mt-0.5 flex flex-wrap items-center gap-1" />
                      {/* 본문 — 지면과 같은 렌더러가 만든 HTML(오너 지시 2026-08-29). 종전에는
                          평문 140자라 수식이 `\dfrac{1}{2}` 같은 원문으로 보여 무슨 문제인지
                          알아볼 수 없는 행이 흔했다.
                          **clamp={false}** — 이 목록은 본문을 **자르지 않는다**(오너 지시
                          2026-08-30). 이 카드는 문제를 열어 목록이 좁아졌을 때만 서는 얼굴이라
                          한 화면에 몇 줄만 들어간다. 그 자리에서 h-16 으로 64px 만 보여 주면
                          발문이 중간에서 끊기는데, 이 칸은 button 안이라 [본문 모두 보기]를
                          둘 수도 없어(아래) **펼 길이 아예 없었다.** 값으로 치르는 것은 행 높이다 —
                          본문 길이를 따라가므로 목록이 계단처럼 서고, 본문이 늦게 오는 행은
                          그때 한 번 뛴다. 넓은 표(ProblemTable)는 행 높이를 옆 칸이 정하므로
                          거기는 그대로 둔다.
                          **showToggle=false** — 이 칸이 button 안에 있다. [본문 모두 보기]를 두면
                          버튼 안의 버튼이 되고, 눌렀을 때 카드의 '문제 열기'까지 함께 돈다.
                          자르지 않으니 이제 누를 일도 없다. */}
                      <ProblemBodyPreview
                        compact
                        clamp={false}
                        showToggle={false}
                        /* 배율 상한 — **표와 같은 크기로 세운다**(오너 지시 2026-08-30).
                           상한이 없으면 배율이 칸 폭을 따라 0.9 까지 올라가 글자가 16px 로 서고,
                           문제를 닫아 표로 넓어지는 순간 12px 로 줄어든다. 같은 목록이 폭에 따라
                           얼굴만 바뀌는 것이지 글자 크기가 바뀔 일이 아니다. */
                        maxScale={LIST_BODY_MAX_SCALE}
                        html={bodies?.[String(it.problem_id)] ?? null}
                        fallback={it.preview || <span className="text-gray-300">본문 미리보기 없음</span>}
                      />
                      {needs.length > 0 && (
                        <span className="mt-1 flex items-center gap-1">
                          <span className="text-[10px] text-gray-400">비어 있음</span>
                          <NeedBadges item={it} />
                        </span>
                      )}
                    </button>
                    {needs.length > 0 && canRunAi && (
                      <button
                        type="button"
                        onClick={() => openAiWindow(it)}
                        disabled={inFlight || inProgress}
                        aria-label={`${it.problem_id}번 문제 분석 요청`}
                        title={inProgress ? '이미 분석이 진행 중입니다' : '비어 있는 항목을 AI가 채웁니다'}
                        className="m-2 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Sparkles aria-hidden="true" className="size-3" />
                        {inFlight ? '요청 중…' : '분석'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── 페이지 ────────────────────────────────────────────────────────────
             화살표 둘과 「10 / 2991」뿐이던 자리를 공용 부품으로 바꿨다(오너 지시 2026-08-28).
             2991 페이지짜리 목록에서 화살표만으로는 원하는 자리에 갈 방법이 없었다 —
             이 부품은 번호를 눌러 뛰고, 현재 번호 칸에 직접 쳐서(Enter) 갈 수도 있다.

             compact 는 한 단 작은 변형이다(오너 「크기는 좀 작게」). 기본값 40px 은 페이지
             전체를 쓰는 목록용이라 이 패널 안에서는 표보다 커 보인다.

             읽는 동안 연타를 막는다 — 부품은 loading 을 모르므로 여기서 가둔다. */}
      {rows.totalPages > 1 && (
        <div className="mt-2 shrink-0">
          <DesktopPagination
            compact
            currentPage={rows.page}
            totalPages={rows.totalPages}
            onPageChange={(p) => { if (!loading) load(applied, p); }}
          />
        </div>
      )}
      {/* 이 패널은 모달이 아니다 — 창이 겹치지 않아 부모의 ESC 를 끌 필요가 없다. */}
      <AiRunModal
        /* 창에도 판정을 건다 — 여는 버튼만 감추면 창은 살아 있고, 나중에 다른 길로 aiTarget 을
           세우는 코드가 생기면 감췄다고 믿은 실행이 조용히 돈다. */
        isOpen={aiTarget != null && canRunAi}
        title="AI 분석 요청"
        summary={aiTarget
          ? `문제 #${aiTarget.problem_id} 를 AI 가 분석합니다. 만들 것은 아래에서 고릅니다.`
          : ''}
        options={aiTarget ? (
          <AnalysisTargetChecks
            targets={aiPicks}
            onChange={setAiPicks}
            presence={presenceFromNeeds(aiTarget)}
            disabled={analyzingIds.has(aiTarget.problem_id)}
          />
        ) : null}
        showOptionPriority
        confirmDisabled={!hasAnyTarget(aiPicks)}
        placeholder="예: 풀이는 학생이 따라오게 단계별로 · 개념은 직접 쓰는 것만"
        confirmText="분석 요청"
        busy={aiTarget != null && analyzingIds.has(aiTarget.problem_id)}
        onConfirm={(userPrompt) => {
          const t = aiTarget;
          const options = toAnalysisOptions(aiPicks);
          setAiTarget(null); // 요청은 왕복이 길다 — 창을 먼저 닫고 목록의 대기 칩으로 진행을 말한다
          if (t) askAnalysis(t, userPrompt, options);
        }}
        onClose={() => setAiTarget(null)}
      />
    </div>
  );
}

export default ProblemSearchPanel;
